import { useEffect, useMemo, useRef, useState } from 'react';
import { daysBetween, isToday, isWeekend } from '@/utils/timeline';

export interface TimelineDayColumn {
  date: string;
  isWeekend: boolean;
  isToday: boolean;
}

export interface TimelineLayout {
  labelWidth: number;
  rowHeight: number;
  stackGap: number;
  /** Total height of the sticky day header (month strip + day row).
   *  Used to align the labels column's empty header cell, the overlays
   *  and the full-height day separators below it. */
  headerHeight: number;
  /** Minimum width per day column. The grid uses minmax(MIN_DAY_WIDTH, 1fr) so
   *  when the available width is less than `totalDays * MIN_DAY_WIDTH`, each day
   *  stays at MIN_DAY_WIDTH and the grid scrolls horizontally (Toggl-style). */
  minDayWidth: number;
  /** Whether the timeline is using its compact, mobile-first layout. */
  isCompact: boolean;
  days: TimelineDayColumn[];
  todayIndex: number;
  totalDays: number;
  gridTemplateColumns: string;
  containerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Computes layout info for the custom Timeline grid.
 *
 * The Timeline uses a CSS Grid with a fixed-width left column for user
 * labels and N day columns. Columns share the remaining width equally
 * (`1fr`), but each column has a minimum width so the grid scrolls
 * horizontally when the window grows past what fits in the viewport —
 * matches Toggl's "drag to load more days" behavior.
 */
export function useTimelineLayout(
  startDate: string,
  endDate: string,
  options: {
    labelWidth?: number;
    rowHeight?: number;
    stackGap?: number;
    headerHeight?: number;
    minDayWidth?: number;
    compactLabelWidth?: number;
    compactMinDayWidth?: number;
  } = {},
): TimelineLayout {
  const rowHeight = options.rowHeight ?? 64;
  const stackGap = options.stackGap ?? 8;
  const headerHeight = options.headerHeight ?? 64;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateWidth = (width?: number) => {
      const nextWidth = width ?? node.getBoundingClientRect().width ?? node.clientWidth;
      setContainerWidth((current) => (current === nextWidth ? current : nextWidth));
    };

    updateWidth();
    const observer = new ResizeObserver(([entry]) => {
      updateWidth(entry?.contentRect.width);
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // The desktop geometry is intentionally unchanged. On narrow screens the
  // user rail becomes a compact Toggl-like resource column and three days
  // remain readable before horizontal scrolling continues the range.
  const isCompact = containerWidth !== null && containerWidth < 640;
  const labelWidth = isCompact
    ? options.compactLabelWidth ?? 76
    : options.labelWidth ?? 200;
  const minDayWidth = isCompact
    ? options.compactMinDayWidth ?? 88
    : options.minDayWidth ?? 120;

  const days = useMemo<TimelineDayColumn[]>(() => {
    return daysBetween(startDate, endDate).map((date) => ({
      date,
      isWeekend: isWeekend(date),
      isToday: isToday(date),
    }));
  }, [startDate, endDate]);

  const todayIndex = useMemo(
    () => days.findIndex((d) => d.isToday),
    [days],
  );

  const gridTemplateColumns = useMemo(
    () => `${labelWidth}px repeat(${days.length}, minmax(${minDayWidth}px, 1fr))`,
    [labelWidth, days.length, minDayWidth],
  );

  return {
    labelWidth,
    rowHeight,
    stackGap,
    headerHeight,
    minDayWidth,
    isCompact,
    days,
    todayIndex,
    totalDays: days.length,
    gridTemplateColumns,
    containerRef,
  };
}
